import { cn } from '@psspl/utils';

/**
 * The official Prakash Software Solutions logo.
 *
 * Source asset: `apps/web/public/prakash-logo-25-years.webp`
 *   400 x 134 px, WebP, RGBA with a fully transparent background.
 *   Measured ink: Prakash blue (#1890C0 range) plus warm orange/gold, and light
 *   greys. There are no dark pixels, so the artwork reads correctly on the dark
 *   surfaces defined in `packages/ui/src/styles/tokens.css` without a filter,
 *   a glow or a recolour.
 *
 * DO NOT replace this with text, an inline SVG, a CSS drawing or an AI-generated
 * mark. The company supplies this asset; see docs/ui-guidelines.md section 1 and
 * open questions Q-06 / Q-07 in docs/requirements.md.
 *
 * The asset is served from `public/`, so Vite returns the file verbatim and the
 * browser never re-encodes it. `width`/`height` are set to the intrinsic pixel
 * size purely to reserve layout space and prevent layout shift.
 */
export const COMPANY_LOGO = {
  src: '/prakash-logo-25-years.webp',
  alt: 'Prakash Software Solutions',
  width: 400,
  height: 134,
  /** Intrinsic aspect ratio, used to size containers that must not shift. */
  ratio: 400 / 134,
} as const;

export type CompanyLogoSize = 'sm' | 'md' | 'lg' | 'xl';

const HEIGHT_CLASS: Record<CompanyLogoSize, string> = {
  sm: 'h-6',
  md: 'h-8',
  lg: 'h-10',
  xl: 'h-16',
};

export interface CompanyLogoProps {
  readonly size?: CompanyLogoSize;
  /**
   * Accessible name. Defaults to the company name. Pass `''` when the logo sits
   * next to a visible text label and would otherwise be announced twice.
   */
  readonly alt?: string;
  readonly className?: string | undefined;
}

export function CompanyLogo({ size = 'md', alt = COMPANY_LOGO.alt, className }: CompanyLogoProps) {
  return (
    <img
      src={COMPANY_LOGO.src}
      alt={alt}
      width={COMPANY_LOGO.width}
      height={COMPANY_LOGO.height}
      decoding="async"
      className={cn('w-auto shrink-0 object-contain', HEIGHT_CLASS[size], className)}
    />
  );
}
